'use client';

import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, AlertCircle, Info, AlertTriangle, X, Clock, Fingerprint, Loader2 } from 'lucide-react';
import { Toast, useToastStore } from '@/lib/store/useToastStore';
import { cn } from '@/lib/utils';

export function ToastItem({ toast }: { toast: Toast }) {
  const { removeToast } = useToastStore();
  const [progress, setProgress] = useState(100);
  const duration = toast.duration === undefined ? (toast.type === 'loading' ? 0 : 5000) : toast.duration;

  useEffect(() => {
    if (duration === 0) return;
    
    setProgress(100);
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(remaining);
      
      if (remaining === 0) {
        clearInterval(interval);
      }
    }, 10);

    return () => clearInterval(interval);
  }, [duration, toast.id, toast.type]);

  const icons = {
    success: <CheckCircle2 className="text-emerald-500" size={18} />,
    error: <AlertCircle className="text-rose-500" size={18} />,
    info: <Info className="text-blue-500" size={18} />,
    warning: <AlertTriangle className="text-amber-500" size={18} />,
    loading: <Loader2 className="text-blue-500 animate-spin" size={18} />,
  };

  const bgColors = {
    success: 'border-emerald-500/10 bg-white/80 dark:bg-slate-900/80',
    error: 'border-rose-500/10 bg-white/80 dark:bg-slate-900/80',
    info: 'border-blue-500/10 bg-white/80 dark:bg-slate-900/80',
    warning: 'border-amber-500/10 bg-white/80 dark:bg-slate-900/80',
    loading: 'border-blue-500/10 bg-white/80 dark:bg-slate-900/80',
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -20, x: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 20, scale: 0.9, transition: { duration: 0.2 } }}
      className={cn(
        "relative w-[380px] overflow-hidden rounded-[20px] border backdrop-blur-2xl shadow-2xl p-5 flex flex-col gap-3 group transition-all duration-300",
        bgColors[toast.type]
      )}
    >
      <div className="flex items-start gap-4">
        <div className="shrink-0 mt-0.5 p-2 bg-slate-50 dark:bg-white/5 rounded-xl border border-black/5 dark:border-white/5 shadow-sm">
          {icons[toast.type]}
        </div>
        
        <div className="flex-1 space-y-1">
          <h4 className="text-[13px] font-bold text-slate-900 dark:text-white ltr:tracking-tight rtl:tracking-normal leading-none">
            {toast.title}
          </h4>
          <p className="text-[12px] text-slate-500 dark:text-slate-400 font-medium leading-relaxed opacity-90">
            {toast.message}
          </p>
        </div>

        <button 
          onClick={() => removeToast(toast.id)}
          className="shrink-0 p-1.5 text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-white/10 rounded-lg transition-all"
        >
          <X size={14} />
        </button>
      </div>

      {toast.metadata && (
        <div className="flex items-center gap-4 pt-3 border-t border-black/5 dark:border-white/5">
          {toast.metadata.ref && (
            <div className="flex items-center gap-2 px-2.5 py-1 bg-slate-50 dark:bg-white/5 rounded-lg border border-black/5 dark:border-white/5">
              <Fingerprint size={10} className="text-slate-400" />
              <span className="text-[8px] font-black uppercase ltr:tracking-widest rtl:tracking-normal text-slate-600 dark:text-slate-400">
                REF: {toast.metadata.ref}
              </span>
            </div>
          )}
          {toast.metadata.timestamp && (
            <div className="flex items-center gap-2 px-2.5 py-1 bg-slate-50 dark:bg-white/5 rounded-lg border border-black/5 dark:border-white/5">
              <Clock size={10} className="text-slate-400" />
              <span className="text-[8px] font-black uppercase ltr:tracking-widest rtl:tracking-normal text-slate-600 dark:text-slate-400">
                {toast.metadata.timestamp}
              </span>
            </div>
          )}
        </div>
      )}

      {duration !== 0 && toast.type !== 'loading' && (
        <div className="absolute bottom-0 left-0 right-0 h-[2.5px] bg-black/5 dark:bg-white/5">
          <motion.div 
            className={cn(
              "h-full",
              toast.type === 'success' && "bg-emerald-500",
              toast.type === 'error' && "bg-rose-500",
              toast.type === 'info' && "bg-blue-500",
              toast.type === 'warning' && "bg-amber-500"
            )}
            initial={{ width: '100%' }}
            animate={{ width: `${progress}%` }}
            transition={{ ease: "linear" }}
          />
        </div>
      )}
    </motion.div>
  );
}


export function ToastContainer() {
  const { toasts } = useToastStore();

  return (
    <div className="fixed top-8 right-8 z-[200] flex flex-col-reverse gap-4 w-full pointer-events-none items-end">
      <AnimatePresence mode="popLayout">
        {toasts.map((toast) => (
          <div key={toast.id} className="pointer-events-auto w-full flex justify-end">
             <ToastItem toast={toast} />
          </div>
        ))}
      </AnimatePresence>
    </div>
  );
}
